/**
 * cart_c7.js — Commerce7 Cart Optional Feature (Valle della Pace)
 *
 * PURPOSE:
 *   The Commerce7 counterpart to features/optional/cart.js (Shopify). Owns the
 *   anonymous C7 cart: lazily creates it, fulfils the header cart icon
 *   (ws-cart-open), and turns WineCard / CartPanel add/update/remove events
 *   into BE calls, then broadcasts the line count for the header badge + cards.
 *
 * KEY DIFFERENCE FROM SHOPIFY cart.js:
 *   The C7 cart is ANONYMOUS — identified purely by cart_id (App Basic Auth on
 *   the BE), no customer_token. Lines are keyed by SKU, not variantId. So the
 *   events here carry { sku } and the Valle CartPanel emits sku-based events.
 *
 * ARCHITECTURE:
 *   The widget never calls Commerce7 directly — every call goes FE → BE → C7 via
 *   core/api.js (public /api/v1/integrations/commerce7/* endpoints; tenant
 *   resolved from the X-Tenant-ID header).
 *
 * ENABLED BY:
 *   tenant.features.commerce7 = true AND tenant.ui_features.show_cart = true.
 *   Wired from variants/valledellapace/index.js register().
 *
 * EVENTS:
 *   in:  ws-cart-open, ws-cart-add {sku, quantity, fromDrawer?},
 *        ws-cart-update {sku, quantity, fromCart?, fromDrawer?},
 *        ws-cart-remove {sku, fromCart?, fromDrawer?}
 *   out: ws-cart-count {count}, ws-cart-busy {active}
 *
 * fromDrawer:
 *   When true (tasting-notes drawer Add to cart / qty), skip posting
 *   "Here is your order summary" — same as Shopify cart.js / Fogarty. Drawer
 *   footer + header badge update via ws-cart-count only.
 *
 * cartId PERSISTENCE:
 *   Stored per tenant in localStorage (key ws_c7cart_<tenantId>), mirroring
 *   core/session.js, so the cart survives reload/minimize/revisit. (The old FE
 *   used a `cartId` cookie; localStorage is the new widget's store — same role.)
 *
 * REFERENCE (old code):
 *   valledellapace/script.js createCart (4566), getShoppingCart (4546),
 *   addToCart (3804), updateQtyToCart (3833), deleteFromCart (3819)
 *
 * EXPORTS:
 *   init() — subscribes to cart events; idempotent.
 */

import * as api from "../../core/api.js";
import { on, emit, getState, setState, addMessage } from "../../core/state.js";
import { getTenantId } from "../../core/configAccessors.js";
import { error } from "../../core/env.js";

const keyFor = (tenantId) => `ws_c7cart_${tenantId || "default"}`;

function loadStoredCartId() {
  try {
    return localStorage.getItem(keyFor(getTenantId())) || null;
  } catch {
    return null;
  }
}

// Persist cartId in state + localStorage (the cart's handle, not a credential).
function persistCartId(id) {
  setState({ cart: { ...getState().cart, id } });
  try {
    if (id) localStorage.setItem(keyFor(getTenantId()), id);
  } catch {
    /* storage blocked — cartId stays in memory for this page only */
  }
}

function getCartId() {
  return getState().cart.id || loadStoredCartId();
}

// Mirror a BE cart into state and broadcast the line count. The C7 add/update/
// remove endpoints return the raw cart ({ id, items[], subTotal, total }), while
// GET cart/items returns ({ cartId, items[], cart{} }). Normalize both here.
function applyCart(resp) {
  const items = Array.isArray(resp?.items) ? resp.items : [];
  const id = resp?.cartId ?? resp?.id ?? getState().cart.id ?? null;
  // Summary fields (subTotal/total) live in resp.cart on GET, top-level on mutate.
  const summary = resp?.cart || resp || null;
  setState({ cart: { ...getState().cart, id, items, summary } });
  const count = items.reduce((n, it) => n + (Number(it.quantity) || 0), 0);
  emit("ws-cart-count", { count });
  return { id, items };
}

function showText(text) {
  // A chat turn's typing indicator (e.g. still fetching the follow-up
  // recommendations for the previous message) can still be showing when the
  // user clicks Add to cart / Proceed to Checkout — those are independent of
  // the chat's streaming state. Clear it so a cart message never gets
  // appended right after a stale "assistant is typing" bubble that looks
  // like it was caused by the cart action.
  emit("ws-chat-typing", { active: false });
  addMessage({ role: "bot", kind: "text", data: { text } });
}

// Old showCartsProducts empty branch (main.js): empty copy + explore prompt +
// red / white / sparkling chips (same-opt-btn → sendMessage).
const EMPTY_CART_EXPLORE = [
  {
    label: "Show me all available red wines",
    value: "Show me all available red wines",
    icon: "bolt",
  },
  {
    label: "Show me all available white wines",
    value: "Show me all available white wines",
    icon: "bolt",
  },
  {
    label: "Show me all available sparkling wines",
    value: "Show me all available sparkling wines",
    icon: "bolt",
  },
];

function showEmpty() {
  emit("ws-cart-count", { count: 0 });
  showText(getState().cart.emptyMessage);
  showText("Would you like to explore some wines?");
  addMessage({
    role: "bot",
    kind: "prompts",
    data: { items: EMPTY_CART_EXPLORE },
  });
}

function showError() {
  showText("Sorry, we couldn't update your cart. Please try again.");
}

// Parity with old showCartsProducts: after a cart change from a wine card, post
// "Here is your order summary" + the cart panel. Edits made inside an existing
// cart panel pass fromCart and re-render in place, so no duplicate summary.
// Tasting-notes drawer passes fromDrawer — badge/footer only, no summary
// (Fogarty cart.js / old tasting footer never called showCartsProducts).
function showOrderSummary(resp) {
  const items = Array.isArray(resp?.items) ? resp.items : [];
  if (!items.length) {
    showEmpty();
    return;
  }
  showText("Here is your order summary");
  addMessage({ role: "bot", kind: "cart", data: { items, cart: resp } });
}

function showCartAfterMutation(resp, { fromCart = false, fromDrawer = false } = {}) {
  if (fromDrawer) return;
  if (!fromCart) showOrderSummary(resp);
}

// Lazily create a C7 cart (old createCart, 4566). Returns the cart_id.
async function ensureCartId() {
  const existing = getCartId();
  if (existing) return existing;
  const cart = await api.c7CreateCart({ channel: "Web" });
  const id = cart?.id ?? cart?.cartId ?? null;
  if (id) persistCartId(id);
  return id;
}

let minimumLoadingMs = 0;
let openingCart = false;

async function openCart() {
  if (openingCart) return;
  const cartId = getCartId();
  if (!cartId && !minimumLoadingMs) {
    showEmpty();
    return;
  }
  openingCart = true;
  if (minimumLoadingMs) emit("ws-chat-typing", { active: true, scroll: true });
  try {
    // Optional tenant timing: keep dots visible for the requested minimum
    // while fetching, and longer if the cart request is still pending.
    const [resp] = await Promise.all([
      cartId ? api.c7GetCart({ cartId }) : Promise.resolve(null),
      minimumLoadingMs
        ? new Promise(resolve => setTimeout(resolve, minimumLoadingMs))
        : Promise.resolve(),
    ]);
    if (minimumLoadingMs) emit("ws-chat-typing", { active: false });
    if (!resp) {
      showEmpty();
      return;
    }
    applyCart(resp);
    showOrderSummary(resp);
  } catch (e) {
    error("[cart_c7] open failed", e);
    showError();
  } finally {
    if (minimumLoadingMs) emit("ws-chat-typing", { active: false });
    openingCart = false;
  }
}

async function addItem({ sku, quantity = 1, fromDrawer = false } = {}) {
  if (!sku) return;
  emit("ws-cart-busy", { active: true });
  try {
    const cartId = await ensureCartId();
    if (!cartId) {
      showError();
      return;
    }
    const cart = cartId
        ? await api.c7AddCartItem({ cartId, sku, quantity })
        : await api.c7CreateCart({ channel: 'Web' });
    applyCart(cart);
    showCartAfterMutation(cart, { fromDrawer });
  } catch (e) {
    error("[cart_c7] add failed", e);
    showError();
  } finally {
    emit("ws-cart-busy", { active: false });
  }
}

async function updateItem({ sku, quantity, fromCart = false, fromDrawer = false } = {}) {
  const cartId = getCartId();
  if (!cartId || !sku) return;
  if (Number(quantity) <= 0) {
    removeItem({ sku, fromCart, fromDrawer });
    return;
  }
  emit("ws-cart-busy", { active: true });
  try {
    const resp = await api.c7UpdateCartItem({ cartId, sku, quantity });
    applyCart(resp);
    showCartAfterMutation(resp, { fromCart, fromDrawer });
  } catch (e) {
    error("[cart_c7] update failed", e);
    showError();
  } finally {
    emit("ws-cart-busy", { active: false });
  }
}

async function removeItem({ sku, fromCart = false, fromDrawer = false } = {}) {
  const cartId = getCartId();
  if (!cartId || !sku) return;
  emit("ws-cart-busy", { active: true });
  try {
    const resp = await api.c7RemoveCartItem({ cartId, sku });
    applyCart(resp);
    showCartAfterMutation(resp, { fromCart, fromDrawer });
  } catch (e) {
    error("[cart_c7] remove failed", e);
    showError();
  } finally {
    emit("ws-cart-busy", { active: false });
  }
}

let started = false;

export async function init({ minimumLoadingMs: delay = 0 } = {}) {
  if (started) return;
  started = true;
  minimumLoadingMs = delay;

  // Restore a persisted cart so the badge + wine cards reflect it on load.
  const stored = loadStoredCartId();
  if (stored) {
    setState({ cart: { ...getState().cart, id: stored } });
    api.c7GetCart({ cartId: stored }).then(applyCart).catch(() => {
      /* stale cartId — leave it; next add reuses or recreates the cart */
    });
  }

  on("ws-cart-open", openCart);
  on("ws-cart-add", addItem);
  on("ws-cart-update", updateItem);
  on("ws-cart-remove", removeItem);
  // "Proceed to checkout" (ws-checkout) is owned by features/optional/checkout_c7.js.
}
